import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, MenuItem, TablePagination, TextField, Tooltip, Typography } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
    BenchmarkRequestError, ComparisonAgreement, DimensionManifest, DimensionSubject, DimensionSubjectsQuery,
    benchmarkService
} from '../../../services/benchmarkService';
import { PanelError } from '../BenchmarkPrimitives';
import { count, scenarioName } from '../BenchmarkVocabulary';
import { resultOutcomeLabel } from '../supplied/results/resultsVocabulary';
import { CellSelection } from './DimensionMatrix';
import { RunScenarios, RunScenariosContext, alertTypologies, raisedByLine } from './runScenarios';
import {
    SIDE_DEFINITIONS, SIDE_LABELS, agreementOrderFor, classLabel, comparisonAgreementMeta, comparisonStatusMeta,
    isPositiveClass, platformReason
} from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

interface ComparisonSubjectsTableProps {
    runKey: number;
    dimension: DimensionManifest;
    /** Cases per agreement kind over the whole dimension, for the chips. */
    agreementCounts?: Partial<Record<ComparisonAgreement, number>>;
    /** The matrix cell the reader selected, if any; it narrows the table to that cell. */
    cell?: CellSelection | null;
    /** The agreement to open on — the disagreement worth reading first. */
    initialAgreement?: ComparisonAgreement;
    onCaseSelect: (caseRef: string) => void;
    selectedCaseRef?: string | null;
}

const OUTCOMES = ['true_positive', 'false_positive'];

const selectSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { color: dsVar('fg-2'), fontSize: '0.8125rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: dsVar('accent-fg') },
    '& .MuiOutlinedInput-root': {
        backgroundColor: dsVar('surface'), color: dsVar('fg-body'), fontSize: '0.875rem',
        '& fieldset': { borderColor: dsVar('line') },
        '&:hover fieldset': { borderColor: dsVar('line') },
        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') }
    },
    '& .MuiSvgIcon-root': { color: dsVar('fg-muted') }
};

const headSx = {
    textAlign: 'left' as const, px: 1.5, py: 1.25, fontSize: '0.6875rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase' as const, color: dsVar('fg-muted'), whiteSpace: 'nowrap' as const
};
const cellSx = { px: 1.5, py: 1.25, fontSize: '0.8125rem', color: dsVar('fg-soft'), verticalAlign: 'middle' };
const monoSx = { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' };

const SCENARIO_COLUMN = { label: 'Alerting scenario', tip: 'The rule in your monitoring system that raised the case.' };
const ALERT_TYPOLOGY_COLUMN = {
    label: 'Alert typology',
    tip: 'What the rules that raised the case are approved as detecting (Ontology → Your vocabulary), as mapped when this run was scored.'
};

/**
 * Each header with what it holds, so a reader can tell the two sides apart without the
 * guide. A run scored before scenario mappings existed has no typologies to show, so it
 * keeps naming the rule.
 */
const columnsFor = (catalog: RunScenarios): { label: string; tip?: string }[] => [
    { label: 'Case' },
    catalog ? ALERT_TYPOLOGY_COLUMN : SCENARIO_COLUMN,
    { label: SIDE_LABELS.institution, tip: SIDE_DEFINITIONS.institution },
    { label: SIDE_LABELS.platform, tip: SIDE_DEFINITIONS.platform },
    { label: SIDE_LABELS.reason, tip: SIDE_DEFINITIONS.reason },
    { label: 'Agreement', tip: 'How the two sides compare on this case.' },
    { label: 'Outcome', tip: 'How the case turned out: true or false positive.' },
    { label: 'SAR', tip: 'Whether a SAR was filed.' }
];

/** The case's alert typologies, its rules beneath; "Not mapped" when no rule has a typology. */
const AlertTypologyCell: React.FC<{ row: DimensionSubject; catalog: RunScenarios }> = ({ row, catalog }) => {
    const ids = row.scenarioIds?.length ? row.scenarioIds : row.scenarioId ? [row.scenarioId] : [];
    if (!ids.length) return <>—</>;
    if (!catalog) return <>{scenarioName(ids[0])}</>;
    const result = alertTypologies(ids, catalog);
    return (
        <Tooltip arrow describeChild title={`Raised by ${raisedByLine(result)}`}>
            <Box component="span" sx={{ display: 'inline-block' }}>
                {result.typologies.length
                    ? <Box component="span" sx={{ fontWeight: 600, color: dsVar('fg-body') }}>{result.typologies.map((t) => t.label).join(', ')}</Box>
                    : <Box component="span" sx={{ fontWeight: 600, color: ink('#FBBF24') }}>Not mapped</Box>}
                <Box component="span" sx={{ display: 'block', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                    {ids.join(' · ')}
                </Box>
            </Box>
        </Tooltip>
    );
};

export const ComparisonAgreementBadge: React.FC<{ kind: ComparisonAgreement; dimension: DimensionManifest }> = ({ kind, dimension }) => {
    const meta = comparisonAgreementMeta(kind, dimension);
    return (
        <Tooltip title={meta.description} arrow>
            <Box
                component="span"
                sx={{
                    display: 'inline-block', px: 1, py: 0.3, borderRadius: '999px', fontSize: '0.6875rem',
                    fontWeight: 700, whiteSpace: 'nowrap', color: ink(meta.color),
                    backgroundColor: `${meta.color}1A`, border: `1px solid ${meta.color}4D`
                }}
            >
                {meta.label}
            </Box>
        </Tooltip>
    );
};

/** One side's value; an empty side says why rather than showing a blank. */
const SideValue: React.FC<{ dimension: DimensionManifest; value: string | null; status?: string | null }> = ({ dimension, value, status }) => {
    if (value === null || value === undefined) {
        return (
            <Box component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                {status ? comparisonStatusMeta(status).label : 'Not recorded'}
            </Box>
        );
    }
    return (
        <Box component="span" sx={{ fontWeight: 700, color: isPositiveClass(dimension, value) ? dsVar('accent-fg') : dsVar('fg-soft') }}>
            {classLabel(value, dimension)}
        </Box>
    );
};

/**
 * The cases behind one dimension: both sides' values, why the agent replay
 * decided as it did, and how the case turned out. Opens on the disagreements, in
 * both directions — the cases the replay raised that the historical record did
 * not, and the cases the historical record raised that the replay missed.
 */
const ComparisonSubjectsTable: React.FC<ComparisonSubjectsTableProps> = ({
    runKey, dimension, agreementCounts, cell, initialAgreement, onCaseSelect, selectedCaseRef
}) => {
    const [agreement, setAgreement] = useState<ComparisonAgreement | undefined>(initialAgreement);
    const [outcome, setOutcome] = useState<string>('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [rows, setRows] = useState<DimensionSubject[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [exporting, setExporting] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const catalog = useContext(RunScenariosContext);
    const columns = useMemo(() => columnsFor(catalog), [catalog]);

    // A matrix cell is its own filter: it names both sides, which fixes the agreement.
    const query = useMemo<Omit<DimensionSubjectsQuery, 'limit' | 'offset'>>(() => (cell
        ? { institution: cell.institution, platform: cell.platform, outcome: outcome || undefined }
        : { agreement, outcome: outcome || undefined }), [cell, agreement, outcome]);

    useEffect(() => { setPage(0); }, [query, dimension.dimensionKey]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        benchmarkService.getDimensionSubjects(runKey, dimension.dimensionKey, {
            ...query, limit: pageSize, offset: page * pageSize
        })
            .then((res) => {
                if (cancelled) return;
                setRows(res?.subjects ?? []);
                setTotal(res?.pagination?.total ?? 0);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load these cases.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [runKey, dimension.dimensionKey, query, page, pageSize, attempt]);

    const exportCsv = useCallback(async () => {
        setExporting(true);
        try {
            await benchmarkService.downloadDimensionSubjectsCsv(runKey, dimension.dimensionKey, query);
        } catch (err) {
            setError(err instanceof BenchmarkRequestError ? err.message : 'Could not export these cases.');
        } finally {
            setExporting(false);
        }
    }, [runKey, dimension.dimensionKey, query]);

    return (
        <Box>
            {!cell && (
                <Box className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Filter by agreement">
                    {agreementOrderFor(dimension).filter((kind) => (agreementCounts?.[kind] ?? 0) > 0 || agreement === kind).map((kind) => {
                        const meta = comparisonAgreementMeta(kind, dimension);
                        const active = agreement === kind;
                        const n = agreementCounts?.[kind];
                        return (
                            <Chip
                                key={kind}
                                label={`${meta.label}${n !== undefined ? ` · ${count(n)}` : ''}`}
                                onClick={() => setAgreement(active ? undefined : kind)}
                                aria-pressed={active}
                                sx={{
                                    fontSize: '0.75rem', fontWeight: 600,
                                    color: active ? dsVar('on-accent') : ink(meta.color),
                                    backgroundColor: active ? meta.color : `${meta.color}14`,
                                    border: `1px solid ${meta.color}4D`,
                                    '&:hover': { backgroundColor: active ? meta.color : `${meta.color}26` }
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            <Box className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <Box className="flex flex-wrap items-center gap-3">
                    {cell && (
                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                            {SIDE_LABELS.institution}: <strong>{classLabel(cell.institution, dimension)}</strong> · {SIDE_LABELS.platform}: <strong>{classLabel(cell.platform, dimension)}</strong>
                        </Typography>
                    )}
                    <TextField
                        select
                        size="small"
                        label="Outcome"
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value)}
                        sx={selectSx}
                    >
                        <MenuItem value="" sx={{ fontSize: '0.875rem' }}>Any outcome</MenuItem>
                        {OUTCOMES.map((o) => (
                            <MenuItem key={o} value={o} sx={{ fontSize: '0.875rem' }}>{resultOutcomeLabel(o)}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Button
                    onClick={exportCsv}
                    disabled={exporting || total === 0}
                    startIcon={exporting ? <CircularProgress size={14} sx={{ color: dsVar('fg-2') }} /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                    sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.8125rem', '&:hover': { color: dsVar('accent-fg') } }}
                >
                    Export CSV
                </Button>
            </Box>

            {error ? (
                <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />
            ) : (
                <>
                    <Box sx={{
                        border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface'), overflowX: 'auto',
                        opacity: loading && rows.length ? 0.45 : 1, transition: 'opacity 120ms ease-out'
                    }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 960, borderCollapse: 'collapse' }}>
                            <Box component="thead">
                                <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                                    {columns.map((c) => (
                                        <Box key={c.label} component="th" sx={headSx}>
                                            {/* describeChild: the tip describes the header; the header's name stays its label. */}
                                            {c.tip ? <Tooltip title={c.tip} arrow describeChild><span>{c.label}</span></Tooltip> : c.label}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {loading && rows.length === 0 && (
                                    <Box component="tr">
                                        <Box component="td" colSpan={columns.length} sx={{ p: 6, textAlign: 'center' }}>
                                            <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                                        </Box>
                                    </Box>
                                )}
                                {!loading && rows.length === 0 && (
                                    <Box component="tr">
                                        <Box component="td" colSpan={columns.length} sx={{ p: 6, textAlign: 'center', color: dsVar('fg-muted'), fontSize: '0.875rem' }}>
                                            No cases match these filters.
                                        </Box>
                                    </Box>
                                )}
                                {rows.map((row) => {
                                    const selected = Boolean(selectedCaseRef) && row.caseRef === selectedCaseRef;
                                    const reason = platformReason(row.detail, row.platformScore, dimension);
                                    return (
                                        <Box
                                            key={row.caseRef}
                                            component="tr"
                                            onClick={() => onCaseSelect(row.caseRef)}
                                            sx={{
                                                borderTop: `1px solid ${dsVar('line')}`, cursor: 'pointer',
                                                backgroundColor: selected ? dsVar('raised') : 'transparent',
                                                boxShadow: selected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                                '&:hover': { backgroundColor: dsVar('raised') }
                                            }}
                                        >
                                            <Box component="td" sx={{ ...cellSx, ...monoSx, color: dsVar('fg-body') }}>{row.caseRef}</Box>
                                            <Box component="td" sx={cellSx}><AlertTypologyCell row={row} catalog={catalog} /></Box>
                                            <Box component="td" sx={cellSx}><SideValue dimension={dimension} value={row.institutionValue} /></Box>
                                            <Box component="td" sx={cellSx}><SideValue dimension={dimension} value={row.platformValue} status={row.status} /></Box>
                                            <Box component="td" sx={{ ...cellSx, color: dsVar('fg-2'), fontSize: '0.75rem', maxWidth: 320 }}>{reason || '—'}</Box>
                                            <Box component="td" sx={cellSx}><ComparisonAgreementBadge kind={row.agreement} dimension={dimension} /></Box>
                                            <Box component="td" sx={cellSx}>{resultOutcomeLabel(row.outcomeClass)}</Box>
                                            <Box component="td" sx={cellSx}>{row.sarFiled === null ? '—' : row.sarFiled ? 'Yes' : 'No'}</Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        onPageChange={(_e, next) => setPage(next)}
                        onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{
                            color: dsVar('fg-2'),
                            '& .MuiTablePagination-selectIcon': { color: dsVar('fg-muted') },
                            '& .MuiIconButton-root.Mui-disabled': { color: dsVar('fg-faint') },
                            '& .MuiIconButton-root': { color: dsVar('fg-2') }
                        }}
                    />
                </>
            )}
        </Box>
    );
};

export default ComparisonSubjectsTable;
